// Geração do documento de Requerimento Individual (HTML para impressão/download em PDF)
// Sem dependências externas: utiliza window.print() na janela gerada (o utilizador
// pode escolher "Salvar como PDF" no diálogo de impressão do navegador).

import { formatDateTimeBR, formatDateUTCBR, maskCPF } from '@/utils/masks'

export interface DocumentoProtocolo {
  numeroProtocolo: string
  nomeCompleto: string
  cpf: string
  funcaoExercida: string
  localTrabalho: string
  dataAbonada: string
  datasAbonadas?: string[]
  coletivo?: boolean
  motivo: string
  descricaoProblema?: string
  descricaoOutro?: string
  status: string
  parecer?: string
  analisadoPorNome?: string
  analisadoEm?: string
  createdAt: string
  /** Quem autorizou/registou o requerimento no sistema. */
  assinaturaAutorizador?: {
    nomeCompleto?: string
    funcao?: string
    cpf?: string
    chave?: string
  }
}

export interface DocumentoCidade {
  nome?: string
  cidadeNome?: string
  logoUrl?: string
  nomeSistema?: string
}

const statusLabel: Record<string, string> = {
  AGUARDANDO_ANALISE: 'AGUARDANDO ANÁLISE',
  APROVADO: 'APROVADO',
  APROVADO_COM_ABONO: 'APROVADO COM ABONO',
  APROVADO_SEM_ABONO: 'APROVADO SEM ABONO',
  REPROVADO: 'REPROVADO',
}

function checkbox(marcado: boolean, label: string): string {
  const marcaEstilo = marcado
    ? 'background-color:#12141A;-webkit-print-color-adjust:exact;print-color-adjust:exact;color-adjust:exact;'
    : 'background-color:#ffffff;'
  return `
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
      <span style="width:14px;height:14px;border:1.5px solid #12141A;display:inline-block;flex-shrink:0;${marcaEstilo}"></span>
      <span style="font-size:11.5px;color:#12141A;${marcado ? 'font-weight:bold;' : ''}">${label}</span>
    </div>
  `
}

export function gerarHtmlRequerimento(p: DocumentoProtocolo, cidade: DocumentoCidade | null): string {
  const nomeSistema = cidade?.nomeSistema || 'PROTOCOLEAI'
  const nomePrefeitura = cidade?.nome || cidade?.cidadeNome || ''
  const logoUrl = cidade?.logoUrl || ''

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8" />
<title>Requerimento ${p.numeroProtocolo}</title>
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
  body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 24px; color: #12141A; }
  .folha { max-width: 720px; margin: 0 auto; border: 2px solid #12141A; border-radius: 10px; padding: 20px 26px; }
  .cabecalho { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #12141A; padding-bottom: 12px; margin-bottom: 14px; }
  .cabecalho img { width: 56px; height: 56px; object-fit: contain; }
  .cabecalho .textos p { margin: 0; }
  .gestao { font-size: 11px; letter-spacing: 1.5px; color: #1E4FBF; font-weight: bold; }
  .prefeitura { font-size: 20px; font-weight: 800; color: #1E4FBF; line-height: 1.15; text-transform: uppercase; }
  .titulo { text-align: center; margin: 6px 0 16px; }
  .titulo h1 { font-size: 18px; margin: 0; letter-spacing: 0.5px; }
  .titulo p { font-size: 10.5px; margin: 3px 0 0; color: #4B5563; }
  .coletivo { text-align: center; font-size: 12px; font-weight: 800; letter-spacing: 1px; color: #92400E; background: #FEF3C7; border: 2px solid #B45309; border-radius: 6px; padding: 7px; margin-bottom: 12px; }
  .campo { border: 1.5px solid #12141A; border-radius: 6px; padding: 8px 10px; margin-bottom: 10px; position: relative; }
  .campo label { position: absolute; top: -8px; left: 10px; background: #fff; padding: 0 4px; font-size: 10px; font-weight: bold; }
  .linha2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .valor { font-size: 13px; padding-top: 4px; min-height: 16px; }
  .motivos { border: 1.5px solid #12141A; border-radius: 6px; padding: 12px 14px; margin-bottom: 10px; }
  .motivos-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 18px; }
  .obs { border: 1.5px solid #12141A; border-radius: 6px; padding: 10px; min-height: 50px; margin-bottom: 14px; font-size: 12px; }
  .rodape { display: flex; justify-content: space-between; font-size: 11px; color: #4B5563; margin-bottom: 10px; }
  .status { text-align: center; font-size: 12.5px; font-weight: bold; padding: 6px; border-radius: 6px; background: #F1F2F4; margin-bottom: 10px; }
  .parecer { border-top: 1px dashed #9CA3AF; padding-top: 10px; font-size: 12px; }
  .assinaturas { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 26px; }
  .assinaturas div { border-top: 1px solid #12141A; padding-top: 4px; text-align: center; font-size: 10px; color: #4B5563; }
  .assinaturas .nome { display: block; font-size: 11.5px; font-weight: bold; color: #12141A; margin-bottom: 1px; }
  .assinaturas .dig { display: block; font-size: 9px; }
  .assinaturas .chave { display: block; font-size: 7.5px; letter-spacing: 0.9px; color: #6B7280; font-family: 'Courier New', monospace; margin-top: 2px; word-break: break-all; }
  .autorizador { margin-top: 18px; border: 1.5px solid #12141A; border-radius: 6px; padding: 9px 12px; }
  .autorizador .rotulo { font-size: 9.5px; font-weight: bold; letter-spacing: 1px; color: #1E4FBF; margin-bottom: 5px; }
  .autorizador .linha { font-size: 11.5px; margin: 1.5px 0; }
  .autorizador .chave { font-size: 8px; letter-spacing: 1.2px; color: #6B7280; font-family: 'Courier New', monospace; margin-top: 5px; word-break: break-all; }
  @media print { body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } .folha { border-width: 1.5px; } }
</style>
</head>
<body>
  <div class="folha">
    <div class="cabecalho">
      ${logoUrl ? `<img src="${logoUrl}" alt="logo" />` : ''}
      <div class="textos">
        <p class="gestao">GESTÃO MUNICIPAL</p>
        <p class="prefeitura">${nomePrefeitura}</p>
      </div>
    </div>

    <div class="titulo">
      <h1>${p.coletivo ? 'REQUERIMENTO COLETIVO' : 'REQUERIMENTO INDIVIDUAL'}</h1>
      <p>AO REGISTRO DE JORNADA TRABALHISTA PELO PONTO ELETRÔNICO — ${nomeSistema}</p>
      <p>Protocolo nº ${p.numeroProtocolo} • Gerado em ${formatDateTimeBR(new Date().toISOString())}</p>
    </div>

    ${p.coletivo
      ? `<div class="coletivo">DOCUMENTO COLETIVO — abrange todos os servidores do local de trabalho indicado</div>`
      : ''}

    ${p.coletivo
      ? `<div class="linha2">
          <div class="campo">
            <label>LOCAL DE TRABALHO</label>
            <div class="valor">${p.localTrabalho}</div>
          </div>
          <div class="campo">
            <label>TIPO DE REQUERIMENTO</label>
            <div class="valor">COLETIVO</div>
          </div>
        </div>`
      : `<div class="linha2">
          <div class="campo">
            <label>NOME COMPLETO</label>
            <div class="valor">${p.nomeCompleto}</div>
          </div>
          <div class="campo">
            <label>CPF</label>
            <div class="valor">${maskCPF(p.cpf)}</div>
          </div>
        </div>

        <div class="linha2">
          <div class="campo">
            <label>FUNÇÃO EXERCIDA</label>
            <div class="valor">${p.funcaoExercida}</div>
          </div>
          <div class="campo">
            <label>LOCAL DE TRABALHO</label>
            <div class="valor">${p.localTrabalho}</div>
          </div>
        </div>`}


    <div class="campo">
      <label>${p.datasAbonadas && p.datasAbonadas.length > 1 ? 'DATAS PARA O ABONO DO REGISTRO' : 'DATA PARA O ABONO DO REGISTRO'}</label>
      <div class="valor">${(p.datasAbonadas && p.datasAbonadas.length ? p.datasAbonadas : [p.dataAbonada]).map(d => formatDateUTCBR(d)).join(' &bull; ')}</div>
    </div>

    <div class="motivos">
      <div class="motivos-grid">
        ${checkbox(p.motivo === 'Compensação de Dias Trabalhados Além da Jornada', 'COMPENSAÇÃO DE DIAS TRABALHADOS ALÉM DA JORNADA')}
        ${checkbox(p.motivo === 'Nojo', 'NOJO')}
        ${checkbox(p.motivo === 'Falta de Internet', 'FALTA DE INTERNET')}
        ${checkbox(p.motivo === 'Capacitação na Área', 'CAPACITAÇÃO NA ÁREA')}
        ${checkbox(p.motivo === 'Problema na Entrada/Saída', 'PROBLEMA NA ENTRADA/SAÍDA')}
        ${checkbox(p.motivo === 'Problema de Reconhecimento Facial', 'PROBLEMA DE RECONHECIMENTO FACIAL')}
        ${checkbox(p.motivo === 'Atestado', 'ATESTADO')}
        ${checkbox(p.motivo === 'Outro', 'OUTRO: DESCREVA')}
        ${checkbox(p.motivo === 'Declaração de Comparecimento', 'DECLARAÇÃO DE COMPARECIMENTO (SEM ABONO)')}
      </div>
    </div>

    <div class="obs">
      <strong style="font-size:10.5px;">OUTRAS JUSTIFICATIVAS</strong>
      <p style="margin:4px 0 0;">${p.descricaoProblema || p.descricaoOutro || '—'}</p>
    </div>

    <div class="status">
      ESTADO: ${statusLabel[p.status] || p.status}
    </div>

    ${p.parecer
      ? `<div class="parecer">
          <strong>Parecer${p.analisadoPorNome ? ` — ${p.analisadoPorNome}` : ''}${p.analisadoEm ? ` em ${formatDateTimeBR(p.analisadoEm)}` : ''}:</strong>
          <p style="margin:4px 0 0;">${p.parecer}</p>
        </div>`
      : ''}

    <div class="rodape">
      <span>Data de criação: ${formatDateTimeBR(p.createdAt)}</span>
      <span>${nomeSistema}</span>
    </div>

    <div class="assinaturas">
      <div>
        <span class="nome">${p.coletivo ? (p.localTrabalho || p.nomeCompleto) : p.nomeCompleto}</span>
        ${p.coletivo ? 'Responsável pelo Local de Trabalho' : (p.cpf ? `CPF: ${maskCPF(p.cpf)}` : 'Requerente')}
      </div>
      <div>
        ${p.assinaturaAutorizador?.nomeCompleto
          ? `<span class="nome">${p.assinaturaAutorizador.nomeCompleto}</span>
             <span class="dig">Assinatura digital${p.assinaturaAutorizador.funcao ? ` — ${p.assinaturaAutorizador.funcao}` : ''}${p.assinaturaAutorizador.cpf ? ` • CPF: ${maskCPF(p.assinaturaAutorizador.cpf)}` : ''}</span>
             ${p.assinaturaAutorizador.chave ? `<span class="chave">${p.assinaturaAutorizador.chave}</span>` : ''}`
          : 'Responsável'}
      </div>
    </div>
  </div>
</body>
</html>`
}

export function abrirJanelaDocumento(html: string, autoPrint = true): void {
  const win = window.open('', '_blank', 'width=860,height=1000')
  if (!win)
    return
  win.document.open()
  win.document.write(html)
  win.document.close()
  if (autoPrint) {
    win.onload = () => {
      win.focus()
      win.print()
    }
    setTimeout(() => {
      try {
        win.focus()
        win.print()
      }
      catch {
        /* silencioso */
      }
    }, 400)
  }
}
